'use client'
import type { InputBaseProps as MUIInputBaseProps } from '@mui/material/InputBase'

import InputBase from '@mui/material/InputBase'

import { styled } from '@mui/material/styles'
import { ValidatedInput } from 'src/types/inputs'
import { isValidInput, isInvalidInput, inputVariantMixins, inputValidationCustomProps } from 'src/components/Particles/InputDecorations'
import { shouldForwardPropFn } from 'src/components/Particles/Particles'
type InputBaseProps = MUIInputBaseProps & {
  hasError?: boolean
}

const shouldForwardProp = shouldForwardPropFn(inputValidationCustomProps)

const StyledTextInput = styled(InputBase, {
  shouldForwardProp,
})<InputBaseProps>(({ theme }) => ({
  // input wrapper providing border and additional styling.
  '&.MuiInputBase-root': {
    // layout
    display: 'flex',
    height: theme.fonts.lineHeight['leading-10'],
    padding: `${theme.sizes.spacing.none} ${theme.sizes.spacing['07']}`,
    alignItems: 'center',
    gap: theme.sizes.spacing['04'],
    alignSelf: 'stretch',
    // style
    borderRadius: theme.sizes.radius.sm,
    '&.Mui-disabled': {
      color: theme.colors.text.disabled,
      backgroundColor: theme.colors.surface.disabled,
      WebkitTextFillColor: theme.colors.text.disabled,
      cursor: 'not-allowed',
    },
  },

  // actual input element, mostly for typography and text styles.
  '& .MuiInputBase-input': {
    // layout
    flex: '1 0 0',
    gap: theme.sizes.spacing['07'],
    alignItems: 'flex-start',
    color: theme.colors.text.primary,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 1,
    // Typography
    fontSize: theme.fonts.size.sm,
    // Workaround to prevent ios from zooming in on input focus, as font sizes smaller than 16px trigger this behavior
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.fonts.size.base,
    },
    fontFamily: theme.fonts.family.body,
    fontStyle: 'normal',
    fontWeight: theme.fonts.weight.normal,
    lineHeight: theme.fonts.lineHeight['leading-5'],
    letterSpacing: theme.fonts.letterSpacing.wide,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    '&::placeholder': {
      color: theme.colors.text.tertiary,
    },
    '&:active': {
      color: theme.colors.text.primary,
    },
    '&:enabled': {
      color: theme.colors.text.primary,
    },
    '&:disabled': {
      color: theme.colors.text.disabled,
      backgroundColor: theme.colors.surface.disabled,
      WebkitTextFillColor: theme.colors.text.disabled,
      cursor: 'not-allowed',
    },
    // TODO; Check if the auto-fill styles can be overridden globally
    '&:-internal-autofill-selected': {
      backgroundColor: 'white !important',
    },
  },
  variants: [
    {
      props: (props: ValidatedInput) => isValidInput(props),
      style: inputVariantMixins(theme).validField || {},
    },
    {
      props: (props: ValidatedInput) => isInvalidInput(props),
      style: inputVariantMixins(theme).invalidField || {},
    },
  ],
}))

export default StyledTextInput
export type { InputBaseProps }
